import { useCallback, useEffect, useState } from 'react';

import { ConfirmDialog } from '../components/ConfirmDialog';
import { Drawer } from '../components/Drawer';
import { Modal } from '../components/Modal';
import { RuleForm } from '../components/RuleForm';
import { api, ApiCallError, type RoutingRule } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  diffRuleUpdate,
  emptyRuleFormState,
  routingRuleToFormState,
  ruleFormStateToPayload,
  type RuleFormState,
} from '../lib/forms';

/**
 * Routing rules — the data-driven gate that decides which feed_items go to
 * the digest, archive, telegram, discord, or blog_drafts destinations. Per
 * ISA Decisions D-B 2026-05-09 ("data-driven, not hardcoded"), this is the
 * surface Steve actually retunes.
 *
 * P6.7 (this iteration) wires create/edit/delete UI on top of P6.5/P6.6.
 */
export function RoutingRules(): JSX.Element {
  const auth = useAuth();
  const idToken = auth.idToken;
  const [rules, setRules] = useState<readonly RoutingRule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<RoutingRule | null>(null);
  const [deleting, setDeleting] = useState<RoutingRule | null>(null);
  const [formBusy, setFormBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const reload = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (idToken === null) return;
      try {
        const result = await api.listRules(idToken, signal);
        setRules(result.rules);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setError(err instanceof ApiCallError ? err.message : 'failed to load rules');
        setRules([]);
      }
    },
    [idToken],
  );

  useEffect(() => {
    const ac = new AbortController();
    void reload(ac.signal);
    return () => ac.abort();
  }, [reload]);

  const toggleActive = useCallback(
    async (r: RoutingRule): Promise<void> => {
      if (idToken === null) return;
      setPendingId(r.id);
      try {
        const result = await api.updateRule(idToken, r.id, { active: !r.active });
        setRules((prev) =>
          prev === null ? prev : prev.map((row) => (row.id === r.id ? result.rule : row)),
        );
      } catch (err) {
        setError(err instanceof ApiCallError ? err.message : 'update failed');
      } finally {
        setPendingId(null);
      }
    },
    [idToken],
  );

  const handleCreate = useCallback(
    async (state: RuleFormState): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      setFormError(null);
      try {
        const payload = ruleFormStateToPayload(state);
        const result = await api.createRule(idToken, payload);
        setRules((prev) => (prev === null ? prev : [result.rule, ...prev]));
        setCreateOpen(false);
      } catch (err) {
        setFormError(err instanceof ApiCallError ? err.message : 'create failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  const handleUpdate = useCallback(
    async (original: RoutingRule, state: RuleFormState): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      setFormError(null);
      try {
        const diff = diffRuleUpdate(original, state);
        if (Object.keys(diff).length === 0) {
          setEditing(null);
          return;
        }
        const result = await api.updateRule(idToken, original.id, diff);
        setRules((prev) =>
          prev === null
            ? prev
            : prev.map((row) => (row.id === original.id ? result.rule : row)),
        );
        setEditing(null);
      } catch (err) {
        setFormError(err instanceof ApiCallError ? err.message : 'update failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  const handleDelete = useCallback(
    async (r: RoutingRule): Promise<void> => {
      if (idToken === null) return;
      setFormBusy(true);
      try {
        await api.deleteRule(idToken, r.id);
        setRules((prev) => (prev === null ? prev : prev.filter((row) => row.id !== r.id)));
        setDeleting(null);
      } catch (err) {
        setError(err instanceof ApiCallError ? err.message : 'delete failed');
      } finally {
        setFormBusy(false);
      }
    },
    [idToken],
  );

  if (rules === null) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 text-center text-2xs font-mono text-ink-muted">
        loading…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <header className="mb-4 flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-base font-mono text-ink">Routing rules</h1>
          <p className="text-2xs font-mono text-ink-muted uppercase tracking-wider mt-0.5">
            {rules.length} total · {rules.filter((r) => r.active).length} active · evaluated by priority desc
          </p>
        </div>
        <button
          onClick={() => { setCreateOpen(true); setFormError(null); }}
          className="text-2xs font-mono uppercase tracking-wider px-3 py-1.5 rounded bg-accent hover:bg-accent-hover text-white"
        >
          + Add rule
        </button>
      </header>

      {error !== null && (
        <p className="text-2xs font-mono text-bad/90 border border-bad/30 bg-bad/10 px-3 py-2 rounded mb-4">
          {error}
        </p>
      )}

      {rules.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted text-2xs font-mono">
          no rules yet — every item lands in the default catch-all archive
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map((r) => (
            <article key={r.id} className="card p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-3">
                    <h2 className="text-sm font-mono text-ink truncate">{r.name}</h2>
                    <span className="text-2xs font-mono text-ink-muted">
                      priority {r.priority ?? '—'}
                    </span>
                    {r.licence_class_override !== null && (
                      <span className="pill-warn">licence: {r.licence_class_override}</span>
                    )}
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {r.destinations.map((d) => (
                      <span key={d} className="pill-dim">{d}</span>
                    ))}
                  </div>

                  <details className="mt-2">
                    <summary className="text-2xs font-mono text-ink-muted uppercase tracking-wider cursor-pointer hover:text-ink-dim">
                      match expression
                    </summary>
                    <pre className="mt-2 p-3 bg-bg-base rounded border border-bg-line/60 text-2xs font-mono text-ink-dim overflow-x-auto">
                      {JSON.stringify(r.match_expression, null, 2)}
                    </pre>
                  </details>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <button
                    onClick={() => { void toggleActive(r); }}
                    disabled={pendingId === r.id}
                    className={
                      r.active
                        ? 'pill-ok hover:bg-ok/20 cursor-pointer'
                        : 'pill-dim hover:bg-bg-line cursor-pointer'
                    }
                    title={r.active ? 'click to deactivate' : 'click to re-activate'}
                  >
                    {pendingId === r.id ? '…' : r.active ? 'on' : 'off'}
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={() => { setEditing(r); setFormError(null); }}
                      className="text-2xs font-mono text-ink-dim hover:text-accent uppercase tracking-wider"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => { setDeleting(r); }}
                      className="text-2xs font-mono text-ink-dim hover:text-bad uppercase tracking-wider"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={createOpen}
        title="Add routing rule"
        onClose={() => { if (!formBusy) setCreateOpen(false); }}
      >
        <RuleForm
          initial={emptyRuleFormState()}
          submitLabel="Create"
          busy={formBusy}
          serverError={formError}
          onSubmit={(s) => { void handleCreate(s); }}
          onCancel={() => { setCreateOpen(false); }}
        />
      </Modal>

      <Drawer
        open={editing !== null}
        title={editing !== null ? `Edit · ${editing.name}` : 'Edit'}
        onClose={() => { if (!formBusy) setEditing(null); }}
      >
        {editing !== null && (
          <RuleForm
            initial={routingRuleToFormState(editing)}
            submitLabel="Save"
            busy={formBusy}
            serverError={formError}
            onSubmit={(s) => { void handleUpdate(editing, s); }}
            onCancel={() => { setEditing(null); }}
          />
        )}
      </Drawer>

      <ConfirmDialog
        open={deleting !== null}
        title="Delete routing rule?"
        message={
          deleting !== null
            ? `${deleting.name} — items routed previously keep their destinations; this only affects future routing.`
            : ''
        }
        confirmLabel="Delete"
        danger
        busy={formBusy}
        onConfirm={() => { if (deleting !== null) void handleDelete(deleting); }}
        onCancel={() => { setDeleting(null); }}
      />
    </div>
  );
}
